import { useEffect, memo } from 'react';

// Type definitions for performance entries
interface LCPEntry extends PerformanceEntry {
  startTime: number;
}

interface FIDEntry extends PerformanceEntry {
  startTime: number;
  processingStart: number;
}

interface CLSEntry extends PerformanceEntry {
  value: number;
  hadRecentInput: boolean;
}

/**
 * Performance monitoring component
 * Tracks Core Web Vitals and other performance metrics
 */
const PerformanceMonitor = memo(() => {
  useEffect(() => {
    // Only run in production with performance API support
    if (process.env.NODE_ENV !== 'production' || !('performance' in window)) {
      return;
    }

    let lcpObserver: PerformanceObserver | null = null;
    let fidObserver: PerformanceObserver | null = null;
    let clsObserver: PerformanceObserver | null = null;

    try {
      // Measure LCP (Largest Contentful Paint)
      lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries() as LCPEntry[];
        const lastEntry = entries[entries.length - 1];
        if (lastEntry) {
          console.log('LCP:', lastEntry.startTime);
        }
      });
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });

      // Measure FID (First Input Delay)
      fidObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries() as FIDEntry[];
        for (const entry of entries) {
          const fid = entry.processingStart - entry.startTime;
          console.log('FID:', fid);
        }
      });
      fidObserver.observe({ type: 'first-input', buffered: true });

      // Measure CLS (Cumulative Layout Shift)
      let clsValue = 0;
      clsObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries() as CLSEntry[];
        for (const entry of entries) {
          if (!entry.hadRecentInput) {
            clsValue += entry.value;
          }
        }
        console.log('CLS:', clsValue);
      });
      clsObserver.observe({ type: 'layout-shift', buffered: true });
    } catch (error) {
      console.warn('Performance monitoring not supported:', error);
    }

    return () => {
      try {
        lcpObserver?.disconnect();
        fidObserver?.disconnect();
        clsObserver?.disconnect();
      } catch (error) {
        console.warn('Error disconnecting performance observers:', error);
      }
    };
  }, []);

  return null; // This component doesn't render anything
});

PerformanceMonitor.displayName = 'PerformanceMonitor';

export default PerformanceMonitor;